import React from 'react';

import {
  CardDisputeAccountStatus,
  CardDisputeMerchandiseOrServices,
  CardDisputeMerchantContactMethod,
} from '@column/api';
import { Checkbox } from '@column/ui';

import { type CardDisputeReason } from '~/lib/disputes/labels';

import {
  type DisputeReasonFormState,
  MERCHANT_CONTACT_DETAIL_LIMITS,
  type MerchantContactDetailField,
  REASON_WITHOUT_DETAILS,
  type ReasonFieldKey,
} from './questionnaire';
import {
  ACCOUNT_STATUS_LABELS,
  FRAUD_TYPE_LABELS,
  MERCHANDISE_OR_SERVICES_LABELS,
  MERCHANT_CONTACT_METHOD_LABELS,
  MERCHANT_RETURN_REFUSAL_REASON_LABELS,
} from './questionnaireLabels';
import { ReasonBooleanField, ReasonDateField, ReasonSelectField, ReasonTextField } from './ReasonFields';

export interface DisputeReasonFormProps {
  reason: CardDisputeReason;
  value: DisputeReasonFormState;
  onChange: (value: DisputeReasonFormState) => void;
  /** Dispute currency; drives the incorrect-amount receipt parsing. */
  currency: string;
  disabled?: boolean;
  /** Missing required-field keys reported by `missingReasonFields`; shown inline per field. */
  missingFields?: ReasonFieldKey[];
  /** Over-limit canceled-recurring contact detail reported by `findOversizedContactDetail`. */
  oversizedField?: MerchantContactDetailField;
}

const FRAUD_TYPE_OPTIONS = Object.entries(FRAUD_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const ACCOUNT_STATUS_OPTIONS = Object.values(CardDisputeAccountStatus).map((value) => ({
  value,
  label: ACCOUNT_STATUS_LABELS[value],
}));
const MERCHANDISE_OR_SERVICES_OPTIONS = Object.values(CardDisputeMerchandiseOrServices).map((value) => ({
  value,
  label: MERCHANDISE_OR_SERVICES_LABELS[value],
}));
const CONTACT_METHOD_OPTIONS = Object.values(CardDisputeMerchantContactMethod).map((value) => ({
  value,
  label: MERCHANT_CONTACT_METHOD_LABELS[value],
}));
const RETURN_REFUSAL_REASON_OPTIONS = Object.entries(MERCHANT_RETURN_REFUSAL_REASON_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * Typed reason-to-fields mapping: one small component per reason, sharing the field primitives.
 * Reasons without caller-supplied details render nothing here; their narrative is collected
 * separately in the evidence step.
 */
export const DisputeReasonForm: React.FC<DisputeReasonFormProps> = ({
  reason,
  value,
  onChange,
  currency,
  disabled,
  missingFields = [],
  oversizedField,
}) => {
  if (REASON_WITHOUT_DETAILS.includes(reason)) {
    return null;
  }

  const missing = (field: ReasonFieldKey): string | undefined =>
    missingFields.includes(field) ? 'This answer is required.' : undefined;

  const oversizeError = (field: MerchantContactDetailField): string | undefined =>
    oversizedField === field ? `This answer is limited to ${MERCHANT_CONTACT_DETAIL_LIMITS[field]} bytes.` : undefined;

  const update = <K extends keyof DisputeReasonFormState>(
    reasonKey: K,
    patch: Partial<NonNullable<DisputeReasonFormState[K]>>
  ) => {
    const current = (value[reasonKey] ?? {}) as NonNullable<DisputeReasonFormState[K]>;
    onChange({ ...value, [reasonKey]: { ...current, ...patch } });
  };

  switch (reason) {
    case 'fraud':
      return (
        <ReasonSelectField
          label="What kind of fraud was this?"
          value={value.fraud?.fraud_type}
          onChange={(fraudType) =>
            update('fraud', { fraud_type: fraudType as NonNullable<DisputeReasonFormState['fraud']>['fraud_type'] })
          }
          options={FRAUD_TYPE_OPTIONS}
          required
          disabled={disabled}
          error={missing('fraud_type')}
          testId="reason-fraud-type"
        />
      );

    case 'no_authorization':
      return (
        <ReasonSelectField
          label="Why should the authorization have been declined?"
          value={value.no_authorization?.account_status}
          onChange={(accountStatus) =>
            update('no_authorization', { account_status: accountStatus as CardDisputeAccountStatus })
          }
          options={ACCOUNT_STATUS_OPTIONS}
          required
          disabled={disabled}
          error={missing('account_status')}
          testId="reason-account-status"
        />
      );

    case 'incorrect_amount': {
      const receiptAmount = value.incorrect_amount?.cardholder_receipt_amount ?? '';
      return (
        <ReasonTextField
          label="Amount on the cardholder's receipt"
          description={`The correct amount in ${currency}, as opposed to the amount actually charged. The claim may not exceed the settled amount minus this value.`}
          value={receiptAmount}
          onChange={(cardholderReceiptAmount) =>
            update('incorrect_amount', { cardholder_receipt_amount: cardholderReceiptAmount })
          }
          placeholder="0.00"
          required
          disabled={disabled}
          error={missing('cardholder_receipt_amount')}
          testId="reason-receipt-amount"
        />
      );
    }

    case 'merchandise_services_not_received':
      return (
        <div className="flex flex-col gap-4">
          <ReasonSelectField
            label="What was purchased?"
            value={value.merchandise_services_not_received?.merchandise_or_services}
            onChange={(merchandiseOrServices) =>
              update('merchandise_services_not_received', {
                merchandise_or_services: merchandiseOrServices as CardDisputeMerchandiseOrServices,
              })
            }
            options={MERCHANDISE_OR_SERVICES_OPTIONS}
            required
            disabled={disabled}
            error={missing('merchandise_or_services')}
            testId="reason-merchandise-or-services"
          />
          <ReasonTextField
            label="What was ordered?"
            value={value.merchandise_services_not_received?.order_details}
            onChange={(orderDetails) => update('merchandise_services_not_received', { order_details: orderDetails })}
            placeholder="Describe the order"
            required
            disabled={disabled}
            error={missing('order_details')}
            multiline
            testId="reason-order-details"
          />
          <ReasonDateField
            label="Expected receipt date"
            value={value.merchandise_services_not_received?.expected_receipt_date}
            onChange={(expectedReceiptDate) =>
              update('merchandise_services_not_received', { expected_receipt_date: expectedReceiptDate })
            }
            required
            disabled={disabled}
            error={missing('expected_receipt_date')}
            testId="reason-expected-receipt-date"
          />
          <ReasonBooleanField
            label="Did the cardholder attempt to resolve the issue with the merchant?"
            value={value.merchandise_services_not_received?.cardholder_attempted_resolution}
            onChange={(cardholderAttemptedResolution) =>
              update('merchandise_services_not_received', {
                cardholder_attempted_resolution: cardholderAttemptedResolution,
              })
            }
            required
            disabled={disabled}
            error={missing('cardholder_attempted_resolution')}
            testId="reason-attempted-resolution"
          />
        </div>
      );

    case 'canceled_recurring_transaction': {
      const details = value.canceled_recurring_transaction ?? { merchant_contact_methods: [] };
      const toggleMethod = (method: CardDisputeMerchantContactMethod, checked: boolean) => {
        const methods = checked
          ? [...details.merchant_contact_methods, method]
          : details.merchant_contact_methods.filter((entry) => entry !== method);
        update('canceled_recurring_transaction', { merchant_contact_methods: methods });
      };
      const hasMethod = (...methods: CardDisputeMerchantContactMethod[]) =>
        methods.some((method) => details.merchant_contact_methods.includes(method));

      return (
        <div className="flex flex-col gap-4">
          <ReasonDateField
            label="Cancellation date"
            description="The date the recurring agreement was canceled with the merchant."
            value={details.cancellation_date}
            onChange={(cancellationDate) =>
              update('canceled_recurring_transaction', { cancellation_date: cancellationDate })
            }
            required
            disabled={disabled}
            error={missing('cancellation_date')}
            testId="reason-cancellation-date"
          />
          <ReasonTextField
            label="How was the agreement canceled?"
            value={details.cancellation_method}
            onChange={(cancellationMethod) =>
              update('canceled_recurring_transaction', { cancellation_method: cancellationMethod })
            }
            placeholder="For example, an email to support or an account-settings change"
            required
            disabled={disabled}
            error={missing('cancellation_method')}
            testId="reason-cancellation-method"
          />

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-foreground">
              How did the cardholder contact the merchant to cancel?
              <span className="text-destructive"> *</span>
            </p>
            {missing('merchant_contact_methods') && (
              <p className="text-xs text-destructive" data-testid="reason-field-error">
                Select at least one contact method.
              </p>
            )}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CONTACT_METHOD_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
                  data-testid={`reason-contact-method-${option.value}`}
                >
                  <Checkbox
                    checked={details.merchant_contact_methods.includes(
                      option.value as CardDisputeMerchantContactMethod
                    )}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      toggleMethod(option.value as CardDisputeMerchantContactMethod, checked === true)
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {hasMethod(CardDisputeMerchantContactMethod.CALL_CENTER, CardDisputeMerchantContactMethod.SMS) && (
            <ReasonTextField
              label="Phone number used to reach the merchant"
              value={details.merchant_contact_phone_number}
              onChange={(merchantContactPhoneNumber) =>
                update('canceled_recurring_transaction', { merchant_contact_phone_number: merchantContactPhoneNumber })
              }
              required
              disabled={disabled}
              maxBytes={MERCHANT_CONTACT_DETAIL_LIMITS.merchant_contact_phone_number}
              error={missing('merchant_contact_phone_number') ?? oversizeError('merchant_contact_phone_number')}
              testId="reason-contact-phone"
            />
          )}
          {hasMethod(CardDisputeMerchantContactMethod.EMAIL) && (
            <ReasonTextField
              label="Email address used to reach the merchant"
              value={details.merchant_contact_email_address}
              onChange={(merchantContactEmailAddress) =>
                update('canceled_recurring_transaction', {
                  merchant_contact_email_address: merchantContactEmailAddress,
                })
              }
              required
              disabled={disabled}
              maxBytes={MERCHANT_CONTACT_DETAIL_LIMITS.merchant_contact_email_address}
              error={missing('merchant_contact_email_address') ?? oversizeError('merchant_contact_email_address')}
              testId="reason-contact-email"
            />
          )}
          {hasMethod(CardDisputeMerchantContactMethod.MAIL, CardDisputeMerchantContactMethod.IN_PERSON) && (
            <ReasonTextField
              label="Address where the merchant was reached"
              value={details.merchant_contact_physical_address}
              onChange={(merchantContactPhysicalAddress) =>
                update('canceled_recurring_transaction', {
                  merchant_contact_physical_address: merchantContactPhysicalAddress,
                })
              }
              required
              disabled={disabled}
              maxBytes={MERCHANT_CONTACT_DETAIL_LIMITS.merchant_contact_physical_address}
              error={missing('merchant_contact_physical_address') ?? oversizeError('merchant_contact_physical_address')}
              testId="reason-contact-address"
            />
          )}
          {hasMethod(CardDisputeMerchantContactMethod.WEBFORM) && (
            <ReasonTextField
              label="App or website used to reach the merchant"
              value={details.merchant_contact_application_name}
              onChange={(merchantContactApplicationName) =>
                update('canceled_recurring_transaction', {
                  merchant_contact_application_name: merchantContactApplicationName,
                })
              }
              required
              disabled={disabled}
              maxBytes={MERCHANT_CONTACT_DETAIL_LIMITS.merchant_contact_application_name}
              error={missing('merchant_contact_application_name') ?? oversizeError('merchant_contact_application_name')}
              testId="reason-contact-application"
            />
          )}

          <ReasonDateField
            label="Account closure date (optional)"
            description="Only when the cardholder closed their account with the merchant."
            value={details.account_closure_date}
            onChange={(accountClosureDate) =>
              update('canceled_recurring_transaction', { account_closure_date: accountClosureDate })
            }
            disabled={disabled}
            testId="reason-account-closure-date"
          />
        </div>
      );
    }

    case 'not_as_described': {
      const details = value.not_as_described ?? {};
      return (
        <div className="flex flex-col gap-4">
          <ReasonSelectField
            label="What was purchased?"
            value={details.merchandise_or_services}
            onChange={(merchandiseOrServices) =>
              update('not_as_described', {
                merchandise_or_services: merchandiseOrServices as CardDisputeMerchandiseOrServices,
              })
            }
            options={MERCHANDISE_OR_SERVICES_OPTIONS}
            required
            disabled={disabled}
            error={missing('merchandise_or_services')}
            testId="reason-merchandise-or-services"
          />
          <ReasonTextField
            label="What was ordered, and how did it differ from what was received?"
            value={details.order_details}
            onChange={(orderDetails) => update('not_as_described', { order_details: orderDetails })}
            placeholder="Describe the order and the difference"
            required
            disabled={disabled}
            error={missing('order_details')}
            multiline
            testId="reason-order-details"
          />
          <ReasonDateField
            label="Date the merchandise or services were received"
            value={details.received_date}
            onChange={(receivedDate) => update('not_as_described', { received_date: receivedDate })}
            required
            disabled={disabled}
            error={missing('received_date')}
            testId="reason-received-date"
          />
          <ReasonBooleanField
            label="Did the cardholder attempt to resolve the issue with the merchant?"
            value={details.cardholder_attempted_resolution}
            onChange={(cardholderAttemptedResolution) =>
              update('not_as_described', { cardholder_attempted_resolution: cardholderAttemptedResolution })
            }
            required
            disabled={disabled}
            error={missing('cardholder_attempted_resolution')}
            testId="reason-attempted-resolution"
          />
          <ReasonBooleanField
            label="Was the merchandise returned to the merchant?"
            value={details.cardholder_returned_merchandise}
            onChange={(cardholderReturnedMerchandise) =>
              update('not_as_described', { cardholder_returned_merchandise: cardholderReturnedMerchandise })
            }
            required
            disabled={disabled}
            error={missing('cardholder_returned_merchandise')}
            testId="reason-returned-merchandise"
          />
          {details.cardholder_returned_merchandise === false && (
            <ReasonBooleanField
              label="Did the cardholder attempt to return the merchandise?"
              value={details.cardholder_attempted_return}
              onChange={(cardholderAttemptedReturn) =>
                update('not_as_described', { cardholder_attempted_return: cardholderAttemptedReturn })
              }
              required
              disabled={disabled}
              error={missing('cardholder_attempted_return')}
              testId="reason-attempted-return"
            />
          )}
          {details.merchandise_or_services === 'merchandise' &&
            details.cardholder_returned_merchandise === false &&
            details.cardholder_attempted_return === true && (
              <>
                <ReasonDateField
                  label="Attempted return date"
                  value={details.attempted_return_date}
                  onChange={(attemptedReturnDate) =>
                    update('not_as_described', { attempted_return_date: attemptedReturnDate })
                  }
                  required
                  disabled={disabled}
                  error={missing('attempted_return_date')}
                  testId="reason-attempted-return-date"
                />
                <ReasonTextField
                  label="How was the return attempted?"
                  value={details.return_attempt_details}
                  onChange={(returnAttemptDetails) =>
                    update('not_as_described', { return_attempt_details: returnAttemptDetails })
                  }
                  required
                  disabled={disabled}
                  error={missing('return_attempt_details')}
                  multiline
                  testId="reason-return-attempt-details"
                />
                <ReasonTextField
                  label="Current disposition of the merchandise"
                  value={details.merchandise_disposition}
                  onChange={(merchandiseDisposition) =>
                    update('not_as_described', { merchandise_disposition: merchandiseDisposition })
                  }
                  placeholder="For example, retained by the cardholder"
                  required
                  disabled={disabled}
                  error={missing('merchandise_disposition')}
                  multiline
                  testId="reason-merchandise-disposition"
                />
                <ReasonSelectField
                  label="Why did the merchant not provide or accept a return?"
                  value={details.merchant_return_refusal_reason}
                  onChange={(merchantReturnRefusalReason) =>
                    update('not_as_described', {
                      merchant_return_refusal_reason: merchantReturnRefusalReason as NonNullable<
                        DisputeReasonFormState['not_as_described']
                      >['merchant_return_refusal_reason'],
                    })
                  }
                  options={RETURN_REFUSAL_REASON_OPTIONS}
                  required
                  disabled={disabled}
                  error={missing('merchant_return_refusal_reason')}
                  testId="reason-return-refusal"
                />
              </>
            )}
        </div>
      );
    }

    case 'counterfeit_merchandise':
      return (
        <div className="flex flex-col gap-4">
          <ReasonTextField
            label="What was ordered?"
            value={value.counterfeit_merchandise?.order_details}
            onChange={(orderDetails) => update('counterfeit_merchandise', { order_details: orderDetails })}
            placeholder="Describe the order"
            required
            disabled={disabled}
            error={missing('order_details')}
            multiline
            testId="reason-order-details"
          />
          <ReasonDateField
            label="Date the counterfeit was discovered"
            value={value.counterfeit_merchandise?.counterfeit_discovered_date}
            onChange={(counterfeitDiscoveredDate) =>
              update('counterfeit_merchandise', { counterfeit_discovered_date: counterfeitDiscoveredDate })
            }
            required
            disabled={disabled}
            error={missing('counterfeit_discovered_date')}
            testId="reason-discovered-date"
          />
          <ReasonTextField
            label="How was the merchandise determined to be counterfeit?"
            value={value.counterfeit_merchandise?.counterfeit_details}
            onChange={(counterfeitDetails) =>
              update('counterfeit_merchandise', { counterfeit_details: counterfeitDetails })
            }
            required
            disabled={disabled}
            error={missing('counterfeit_details')}
            multiline
            testId="reason-counterfeit-details"
          />
          <ReasonTextField
            label="Current disposition of the merchandise"
            value={value.counterfeit_merchandise?.merchandise_disposition}
            onChange={(merchandiseDisposition) =>
              update('counterfeit_merchandise', { merchandise_disposition: merchandiseDisposition })
            }
            placeholder="For example, retained by the cardholder"
            required
            disabled={disabled}
            error={missing('merchandise_disposition')}
            multiline
            testId="reason-merchandise-disposition"
          />
        </div>
      );

    case 'credit_not_processed':
      return (
        <ReasonDateField
          label="Credit voucher date"
          description="The date the merchant issued the credit voucher or transaction receipt that was never processed."
          value={value.credit_not_processed?.credit_voucher_date}
          onChange={(creditVoucherDate) => update('credit_not_processed', { credit_voucher_date: creditVoucherDate })}
          required
          disabled={disabled}
          error={missing('credit_voucher_date')}
          testId="reason-credit-voucher-date"
        />
      );

    default:
      return null;
  }
};
